import { CameraModelTooltip } from '#~/common/tooltip/camera-model.jsx';
import { Brand, Model } from '#~/device/components/config/index.js';
import { useBrands, useModels } from '#~/device/registry/hooks.js';
import type { Template } from '#~/device/registry/index.js';
import { Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import type { HtmlAttributes } from '@studio/design-system/props';
import { DefaultModelId } from '@studio/models/device';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { shallowEqual } from 'fast-equals';
import { pick } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { RequireAtLeastOne } from 'type-fest';
import { shallow } from 'zustand/shallow';
// Mount <AutoConfiguredLabel> on Brand + Model Groups via the design-
// system `annotation` slot so the producer sees that those values
// arrived from Extended Discovery's known-model template (not their
// own typing). The label component reads the depot slice and self-
// evicts when the live value diverges.
// Per `ui-architecture` (ai.knowledge/global): Group.annotation is the
// design-system primitive for trailing label decoration — reuse, do
// not reinvent.
import { AutoConfiguredLabel } from '../auto-configured-label.jsx';
import { useProducer, useStore } from '../store/hooks.js';
import type { Selection } from './types.js';

/**
 * Picker selection for a saved driver. DefaultModelId is the fallback every unidentified camera is
 * saved with, not a brand choice: leave Brand unselected so the auto-select below never rewrites it
 * to the "Other" driver and "Other – brand is not listed" stays a deliberate pick.
 */
const toSelection = (
  config: T_Maybe<Template>,
  modelId: T_Maybe<M_Model['id']>
): Required<Selection> =>
  modelId === DefaultModelId
    ? { brandId: undefined, modelId: undefined }
    : { brandId: config?.model.brandId, modelId };

/** Manufacturer `<Details>` component. */
export const Details = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const { config, ...store } = useStore(
    (µ) => ({
      // `candidateId` is read here (alongside existing modelId/name
      // pick) so the `<AutoConfiguredLabel>` mounts can address the
      // depot slice for this device (keyed by `(deviceId, fieldPath)`).
      candidateId: µ.candidate.id,
      ...pick(µ.candidate, ['modelId', 'name']),
      ...pick(µ.manufacturer, ['config', 'preferredModelId'])
    }),
    shallow
  );

  const [selection, setSelection] = useState<Selection>(() => toSelection(config, store.modelId));

  const brands = useBrands();
  const models = useModels(selection.brandId);

  const syncSelection = useLatestFn((µ: RequireAtLeastOne<Selection>) => {
    const nextSelection = { ...selection, ...µ };

    // Did the selection actually change?
    if (shallowEqual(selection, nextSelection)) {
      return;
    }

    setSelection(nextSelection);

    const { modelId } = nextSelection;

    // Update the candidate if the model changed.
    if (modelId && modelId !== store.modelId) {
      producer((draft) => {
        draft.candidate.modelId = modelId;
      });
    }
  });

  // React to changes in the configuration.
  useEffect(() => {
    // Keep the selected brand in sync with the configuration
    if (config) {
      syncSelection(toSelection(config, config.model.id));
    }
  }, [config]);

  const handleBrandChange = useLatestFn((brandId: M_Brand['id']) => {
    syncSelection({ brandId, modelId: store.preferredModelId[brandId] });
  });

  const handleModelChange = useLatestFn((modelId: M_Model['id']) => {
    syncSelection({ modelId });
  });

  const handleNameChange = useLatestFn((nextValue: string, event: SyntheticEvent) => {
    const nextName = toString(nextValue);

    if (nextName && nextName !== store.name) {
      producer((draft) => {
        draft.nameOverride = nextName;
      });
    } else {
      event.preventDefault();
    }
  });
  const selectedBrand = Object.entries(brands?.items ?? {})?.find?.(
    ([, { id }]) => id === selection.brandId
  );
  const isOtherBrandSelected = selectedBrand?.[1]?.name.toLowerCase().includes('other');

  useEffect(() => {
    if (!isOtherBrandSelected) {
      return;
    }

    const otherModel = Object.entries(models?.items ?? {})
      .map(([, model]) => model)
      ?.find(({ name }) => name.toLowerCase().includes('other'));

    if (otherModel?.id) {
      handleModelChange(otherModel.id);
    }
  }, [isOtherBrandSelected, models, brands]);

  const memoizedSelection = useMemo(
    () => toAccessor(selection.brandId, handleBrandChange),
    [selection.brandId, handleBrandChange]
  );

  const memoizedModelSelection = useMemo(
    () => toAccessor(selection.modelId as M_Model['id'], handleModelChange),
    [selection.modelId, handleModelChange]
  );

  return (
    <Column {...props}>
      {/* Brand Group surfaces "Auto Configured" beside the field label
          when the slice records `(candidateId, 'manufacturer.brand')`.
          Field path MUST match the value recorded by the multi-add and
          single-add recording paths — `manufacturer.brand` is the
          canonical key. The live `value` is the selected `brandId` (a
          string id), which is what the recording paths store. */}
      <Group
        annotation={
          <AutoConfiguredLabel
            deviceId={store.candidateId}
            fieldPath='manufacturer.brand'
            value={selection.brandId}
          />
        }
        label='Camera brand'
      >
        <Brand brands={brands} selection={memoizedSelection} />
      </Group>

      {(selectedBrand ? !isOtherBrandSelected : true) && (
        /* Model Group already carries an
            `annotation={<CameraModelTooltip />}` — the design-system
            `annotation` slot only takes ONE node. Reconcile by passing
            a fragment containing both the tooltip and the label so
            both render on the trailing edge. Per `stay-in-scope`
            (ai.knowledge/global): we do not modify the Group
            component itself — fragment composition stays in our
            consumer. */
        <Group
          annotation={
            <>
              <CameraModelTooltip />
              <AutoConfiguredLabel
                deviceId={store.candidateId}
                fieldPath='manufacturer.model'
                value={selection.modelId}
              />
            </>
          }
          label='Camera model'
        >
          <Model
            brandId={memoizedSelection[0]}
            key={selection.brandId}
            models={models}
            selection={memoizedModelSelection}
          />
        </Group>
      )}

      {/* The `name` field is intentionally NOT labeled — the producer
          routinely overwrites the auto-filled name with their own
          nickname; carrying the label here would evict immediately
          and add noise. */}
      <Group label='Camera nickname'>
        <Mediator onCommit={handleNameChange} value={store.name} />
      </Group>
    </Column>
  );
};
